// CLI v0.8 — `grid wire repo` implementation.
//
// Binds a GitHub repo to the linked entity for auto-deploy on push.
//
// Server contract:
//   POST   /api/v2/entities/:id/wire-repo  — { repo_url, branch? }
//   DELETE /api/v2/entities/:id/wire-repo  — no body
//
// Per spec v17 §12 and Phase 1 master plan M5.

import { Command } from 'commander';
import { type IV2Client, getV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { handleV2Error } from '../handle-error.js';
import { requireLink } from '../services/link.js';
import { getActiveOrg } from '../active-org.js';
import { validateLinkVsActiveOrg } from '../services/link.js';
import type { LinkArtifact } from '../services/link.js';
import { CloudGridError } from '../utils.js';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface WireRepoOpts {
  branch?: string;
  remove?: boolean;
}

interface WireRepoResponse {
  entity_id: string;
  repo: string | null;
  branch: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core — testable, no process.exit, injected client + link
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Testable core for `grid wire repo`.
 *
 * - `repoUrl`  — GitHub repo URL (required when not removing)
 * - `opts`     — CLI flags: `branch`, `remove`
 * - `client`   — injected IV2Client (real or mock)
 * - `link`     — injected LinkArtifact (callers may pass `requireLink()` or a fake)
 *
 * Throws on all error paths; the CLI action handler catches and calls
 * `handleV2Error`. GITHUB_APP_NO_ACCESS is intercepted here to emit an
 * actionable install hint to stderr before re-throwing.
 */
export async function runWireRepo(
  repoUrl: string | undefined,
  opts: WireRepoOpts,
  client: IV2Client,
  link: LinkArtifact,
): Promise<void> {
  const entityId = link.entity_id;

  // ── DELETE (unwire) ────────────────────────────────────────────────────

  if (opts.remove) {
    await client.del<void>(`/api/v2/entities/${entityId}/wire-repo`);
    process.stdout.write('✓ Repo wiring removed.\n');
    process.stdout.write('  Auto-deploy disabled.\n');
    return;
  }

  // ── POST (wire) ────────────────────────────────────────────────────────

  if (!repoUrl) {
    throw new CloudGridError(
      'Repo URL is required. Usage: grid wire repo <github-url> [--branch <branch>]',
    );
  }

  // Extract owner/repo for display from the URL.
  // The server canonicalises the URL; we do a best-effort parse for display.
  const repoDisplay = extractOwnerRepo(repoUrl);
  const entityName = repoDisplay ? repoDisplay.split('/').pop() ?? repoDisplay : repoUrl;

  const body: Record<string, string> = { repo_url: repoUrl };
  if (opts.branch) body.branch = opts.branch;

  // Emit the in-progress line before the POST fires so the user sees feedback
  // immediately on slow connections. Show the best-parsed form we have; the
  // server returns the canonical owner/repo form in the response.
  process.stdout.write(`Wiring ${entityName} to ${repoDisplay ? `github.com/${repoDisplay}` : repoUrl}...\n`);

  let result: WireRepoResponse;
  try {
    result = await client.post<WireRepoResponse>(
      `/api/v2/entities/${entityId}/wire-repo`,
      body,
    );
  } catch (err) {
    if (err instanceof V2ApiError && err.code === 'GITHUB_APP_NO_ACCESS') {
      // Re-throw with install hints in `details.hint` so `handleV2Error` prints:
      //   Error: <message>
      //   Install it at: ...
      //   Then re-run: ...
      // in the right order. handleV2Error already iterates `details` for hints.
      throw new V2ApiError(
        err.status,
        err.code,
        err.message,
        [
          { hint: `Install it at: https://github.com/apps/cloudgrid/installations/new` },
          { hint: `Then re-run: grid wire repo ${repoUrl}` },
        ],
        err.requestId,
      );
    }
    throw err;
  }

  const serverBranch = result.branch ?? opts.branch ?? 'main';
  process.stdout.write(`  Default branch: ${serverBranch}\n`);
  process.stdout.write(`  Auto-deploy on push: enabled\n`);
  process.stdout.write(`  Server-side mapping written.\n`);
  process.stdout.write(`  GitHub App permissions verified.\n`);
  process.stdout.write(`✓ Wired.\n`);
  process.stdout.write(`  Push to ${serverBranch} now triggers grid plug.\n`);
}

/**
 * Extract `owner/repo` from a GitHub URL or `owner/repo` string for display.
 * Returns null when the input doesn't match a recognizable pattern.
 * The server is the authoritative validator; this is display-only.
 */
function extractOwnerRepo(url: string): string | null {
  const trimmed = url.trim().replace(/\.git$/, '');
  try {
    const u = new URL(trimmed);
    if (u.hostname !== 'github.com') return null;
    const parts = u.pathname.replace(/^\//, '').split('/');
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return `${parts[0]}/${parts[1]}`;
    }
    return null;
  } catch {
    // Not a URL — try bare owner/repo form.
    if (/^[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+$/.test(trimmed)) return trimmed;
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Commander action — reads link, gets client, calls core
// ─────────────────────────────────────────────────────────────────────────────

export const wireRepoCommand = new Command('repo')
  .description('Bind a GitHub repo to the linked entity for auto-deploy')
  .argument('[github-url]', 'GitHub repository URL (https://github.com/owner/repo)')
  .option('--branch <branch>', 'Branch to track (defaults to repo default branch)')
  .option('--remove', 'Remove the repo binding and disable auto-deploy')
  .action(async (repoUrl: string | undefined, opts: WireRepoOpts) => {
    try {
      const link = requireLink();
      // Build the client first; getActiveOrg now resolves against the
      // server. The client's slug header is set from active_org_slug
      // initially, but we rebuild with the resolved slug below so any
      // mismatch between local config and server reality stays correct.
      const client = await getV2Client();
      const activeOrg = await getActiveOrg(client);
      validateLinkVsActiveOrg(link, activeOrg);
      const scopedClient = await getV2Client(activeOrg.slug);
      await runWireRepo(repoUrl, opts, scopedClient, link);
    } catch (err) {
      await handleV2Error(err);
    }
  });
